import dayjs from 'dayjs';

export type ChartType = 'LINE' | 'BAR' | 'DONUT' | 'STAT';
export type Aggregation = 'LATEST' | 'SUM' | 'AVG' | 'COUNT' | 'PERCENT_TRUE';
export type FieldType = 'NUMBER' | 'TEXT' | 'SELECT' | 'BOOLEAN' | 'CLOCK';

export interface FieldDefinition {
  id: string;
  name: string;
  type: FieldType;
  options?: string[];
}

export interface DayValueEntry {
  fieldId: string;
  value: string | null;
}

export interface DayInput {
  id: string;
  date: string;
  customFieldValues: DayValueEntry[];
}

export interface SeriesPoint {
  date: string;
  value: number | null;
}

export interface EChartsSeriesResult {
  kind: 'line' | 'bar' | 'donut' | 'stat' | 'empty';
  categories?: string[];
  series?: { name: string; data: (number | null)[] }[];
  donutData?: { name: string; value: number }[];
  statValue?: string;
  statSubtitle?: string;
  yAxisFormatter?: string | ((val: number) => string);
  yAxisMin?: number;
  yAxisMax?: number;
}

const TIME_FORMAT = 'HH:mm';

const parseClockToDecimalHours = (value: string): number | null => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  if (isNaN(hours) || isNaN(minutes)) return null;
  return hours + minutes / 60;
};

const formatDecimalHoursToTime = (decimal: number): string => {
  const totalMinutes = Math.round(decimal * 60);
  const hours = Math.floor(totalMinutes / 60) % 24;
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

export const buildGapFreeDays = (daysCount: number): dayjs.Dayjs[] =>
  Array.from({ length: daysCount }, (_, i) =>
    dayjs().subtract(daysCount - 1 - i, 'day').startOf('day')
  );

const dayToDateMap = (days: DayInput[]): Map<string, DayInput> => {
  const map = new Map<string, DayInput>();
  days.forEach((d) => {
    const dDate = dayjs(isNaN(Number(d.date)) ? d.date : Number(d.date));
    map.set(dDate.format('YYYY-MM-DD'), d);
  });
  return map;
};

const valuesByFieldId = (day: DayInput | undefined, fieldId: string): string | null => {
  if (!day) return null;
  const entry = day.customFieldValues.find((v) => v.fieldId === fieldId);
  if (!entry) return null;
  return entry.value;
};

const toNumber = (raw: string | null): number | null => {
  if (raw === null || raw === undefined || raw === '') return null;
  const num = parseFloat(raw);
  return isNaN(num) ? null : num;
};

const toBoolean = (raw: string | null): boolean | null => {
  if (raw === null || raw === undefined || raw === '') return null;
  return raw === 'true';
};

export const buildWidgetSeries = (
  field: FieldDefinition,
  days: DayInput[],
  daysCount: number,
  chartType: ChartType,
  aggregation: Aggregation
): EChartsSeriesResult => {
  const weekDays = buildGapFreeDays(daysCount);
  const categories = weekDays.map((d) => d.format('DD/MM'));
  const dayMap = dayToDateMap(days);

  if (chartType === 'STAT') {
    return buildStatSeries(field, days, weekDays, aggregation);
  }

  if (field.type === 'NUMBER') {
    return buildNumberSeries(field, weekDays, dayMap, categories, chartType, aggregation);
  }

  if (field.type === 'CLOCK') {
    return buildClockSeries(field, weekDays, dayMap, categories, chartType, aggregation);
  }

  if (field.type === 'BOOLEAN') {
    return buildBooleanSeries(field, weekDays, dayMap, categories, chartType, aggregation);
  }

  if (field.type === 'SELECT') {
    return buildSelectSeries(field, days, weekDays, dayMap, categories, chartType, aggregation);
  }

  return { kind: 'empty' };
};

const buildStatSeries = (
  field: FieldDefinition,
  days: DayInput[],
  weekDays: dayjs.Dayjs[],
  aggregation: Aggregation
): EChartsSeriesResult => {
  const dayMap = dayToDateMap(days);

  if (field.type === 'TEXT') {
    let latest: string | null = null;
    for (const day of weekDays) {
      const raw = valuesByFieldId(dayMap.get(day.format('YYYY-MM-DD')), field.id);
      if (raw && raw.trim() !== '') latest = raw;
    }
    return {
      kind: 'stat',
      statValue: latest ? (latest.length > 40 ? latest.slice(0, 40) + '…' : latest) : '—',
      statSubtitle: 'Latest entry',
    };
  }

  if (field.type === 'CLOCK') {
    let latest: string | null = null;
    for (const day of weekDays) {
      const raw = valuesByFieldId(dayMap.get(day.format('YYYY-MM-DD')), field.id);
      if (raw && /^\d{1,2}:\d{2}$/.test(raw.trim())) latest = raw.trim();
    }
    return {
      kind: 'stat',
      statValue: latest || '—',
      statSubtitle: 'Latest time',
    };
  }

  if (field.type === 'NUMBER') {
    const values: number[] = [];
    for (const day of weekDays) {
      const raw = valuesByFieldId(dayMap.get(day.format('YYYY-MM-DD')), field.id);
      const num = toNumber(raw);
      if (num !== null) values.push(num);
    }
    if (values.length === 0) return { kind: 'stat', statValue: '—', statSubtitle: 'No data' };
    if (aggregation === 'SUM') {
      return { kind: 'stat', statValue: formatStatNumber(values.reduce((a, b) => a + b, 0)), statSubtitle: 'Sum' };
    }
    if (aggregation === 'AVG') {
      const avg = values.reduce((a, b) => a + b, 0) / values.length;
      return { kind: 'stat', statValue: formatStatNumber(avg), statSubtitle: 'Average' };
    }
    if (aggregation === 'COUNT') {
      return { kind: 'stat', statValue: String(values.length), statSubtitle: 'Count of entries' };
    }
    return { kind: 'stat', statValue: formatStatNumber(values[values.length - 1]), statSubtitle: 'Latest' };
  }

  if (field.type === 'BOOLEAN') {
    if (aggregation === 'COUNT') {
      let count = 0;
      for (const day of weekDays) {
        const raw = valuesByFieldId(dayMap.get(day.format('YYYY-MM-DD')), field.id);
        if (toBoolean(raw) === true) count++;
      }
      return { kind: 'stat', statValue: String(count), statSubtitle: 'True count' };
    }
    if (aggregation === 'PERCENT_TRUE') {
      let trueCount = 0;
      let totalCount = 0;
      for (const day of weekDays) {
        const raw = valuesByFieldId(dayMap.get(day.format('YYYY-MM-DD')), field.id);
        const bool = toBoolean(raw);
        if (bool !== null) {
          totalCount++;
          if (bool) trueCount++;
        }
      }
      const pct = totalCount === 0 ? 0 : Math.round((trueCount / totalCount) * 100);
      return { kind: 'stat', statValue: `${pct}%`, statSubtitle: 'Percent true' };
    }
    let latest = '—';
    for (const day of weekDays) {
      const raw = valuesByFieldId(dayMap.get(day.format('YYYY-MM-DD')), field.id);
      const bool = toBoolean(raw);
      if (bool !== null) latest = bool ? 'Yes' : 'No';
    }
    return { kind: 'stat', statValue: latest, statSubtitle: 'Latest' };
  }

  if (field.type === 'SELECT') {
    if (aggregation === 'COUNT') {
      let count = 0;
      for (const day of weekDays) {
        const raw = valuesByFieldId(dayMap.get(day.format('YYYY-MM-DD')), field.id);
        if (raw && raw.trim() !== '') count++;
      }
      return { kind: 'stat', statValue: String(count), statSubtitle: 'Count of entries' };
    }
    let latest = '—';
    for (const day of weekDays) {
      const raw = valuesByFieldId(dayMap.get(day.format('YYYY-MM-DD')), field.id);
      if (raw && raw.trim() !== '') latest = raw;
    }
    return { kind: 'stat', statValue: latest, statSubtitle: 'Latest' };
  }

  return { kind: 'stat', statValue: '—' };
};

const formatStatNumber = (n: number): string => {
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(2);
};

const buildNumberSeries = (
  field: FieldDefinition,
  weekDays: dayjs.Dayjs[],
  dayMap: Map<string, DayInput>,
  categories: string[],
  chartType: ChartType,
  aggregation: Aggregation
): EChartsSeriesResult => {
  if (aggregation === 'SUM' || aggregation === 'AVG') {
    const collected: number[] = [];
    for (const day of weekDays) {
      const raw = valuesByFieldId(dayMap.get(day.format('YYYY-MM-DD')), field.id);
      const num = toNumber(raw);
      if (num !== null) collected.push(num);
    }
    if (aggregation === 'SUM') {
      return { kind: 'stat', statValue: formatStatNumber(collected.reduce((a, b) => a + b, 0)), statSubtitle: 'Sum' };
    }
    const avg = collected.length === 0 ? 0 : collected.reduce((a, b) => a + b, 0) / collected.length;
    return { kind: 'stat', statValue: formatStatNumber(avg), statSubtitle: 'Average' };
  }

  if (aggregation === 'COUNT') {
    const data = weekDays.map((day) => {
      const raw = valuesByFieldId(dayMap.get(day.format('YYYY-MM-DD')), field.id);
      return toNumber(raw) !== null ? 1 : 0;
    });
    return {
      kind: chartType === 'BAR' ? 'bar' : 'line',
      categories,
      series: [{ name: field.name, data }],
    };
  }

  const data = weekDays.map((day) => {
    const raw = valuesByFieldId(dayMap.get(day.format('YYYY-MM-DD')), field.id);
    return toNumber(raw);
  });
  return {
    kind: chartType === 'BAR' ? 'bar' : 'line',
    categories,
    series: [{ name: field.name, data }],
  };
};

const buildClockSeries = (
  field: FieldDefinition,
  weekDays: dayjs.Dayjs[],
  dayMap: Map<string, DayInput>,
  categories: string[],
  chartType: ChartType,
  aggregation: Aggregation
): EChartsSeriesResult => {
  const data = weekDays.map((day) => {
    const raw = valuesByFieldId(dayMap.get(day.format('YYYY-MM-DD')), field.id);
    if (!raw) return null;
    return parseClockToDecimalHours(raw);
  });

  return {
    kind: chartType === 'BAR' ? 'bar' : 'line',
    categories,
    series: [{ name: field.name, data }],
    yAxisMin: 0,
    yAxisMax: 24,
    yAxisFormatter: (val: number) => formatDecimalHoursToTime(val),
  };
};

const buildBooleanSeries = (
  field: FieldDefinition,
  weekDays: dayjs.Dayjs[],
  dayMap: Map<string, DayInput>,
  categories: string[],
  chartType: ChartType,
  aggregation: Aggregation
): EChartsSeriesResult => {
  if (chartType === 'DONUT') {
    let trueCount = 0;
    let falseCount = 0;
    for (const day of weekDays) {
      const raw = valuesByFieldId(dayMap.get(day.format('YYYY-MM-DD')), field.id);
      const bool = toBoolean(raw);
      if (bool === true) trueCount++;
      else if (bool === false) falseCount++;
    }
    return {
      kind: 'donut',
      donutData: [
        { name: 'Yes', value: trueCount },
        { name: 'No', value: falseCount },
      ],
    };
  }

  if (aggregation === 'COUNT') {
    const data = weekDays.map((day) => {
      const raw = valuesByFieldId(dayMap.get(day.format('YYYY-MM-DD')), field.id);
      return toBoolean(raw) === true ? 1 : 0;
    });
    return {
      kind: chartType === 'BAR' ? 'bar' : 'line',
      categories,
      series: [{ name: field.name, data }],
    };
  }

  const data = weekDays.map((day) => {
    const raw = valuesByFieldId(dayMap.get(day.format('YYYY-MM-DD')), field.id);
    const bool = toBoolean(raw);
    if (bool === null) return null;
    return bool ? 100 : 0;
  });
  return {
    kind: chartType === 'BAR' ? 'bar' : 'line',
    categories,
    series: [{ name: field.name, data }],
    yAxisFormatter: '{value}%',
  };
};

const buildSelectSeries = (
  field: FieldDefinition,
  days: DayInput[],
  weekDays: dayjs.Dayjs[],
  dayMap: Map<string, DayInput>,
  categories: string[],
  chartType: ChartType,
  aggregation: Aggregation
): EChartsSeriesResult => {
  const options = field.options || [];

  if (chartType === 'DONUT') {
    const counts: Record<string, number> = {};
    options.forEach((opt) => (counts[opt] = 0));
    for (const day of weekDays) {
      const raw = valuesByFieldId(dayMap.get(day.format('YYYY-MM-DD')), field.id);
      if (raw && raw.trim() !== '' && counts[raw] !== undefined) counts[raw]++;
    }
    return {
      kind: 'donut',
      donutData: Object.entries(counts).map(([name, value]) => ({ name, value })),
    };
  }

  if (chartType === 'BAR' && aggregation !== 'COUNT') {
    const series = options.map((opt) => ({
      name: opt,
      data: weekDays.map((day) => {
        const raw = valuesByFieldId(dayMap.get(day.format('YYYY-MM-DD')), field.id);
        return raw === opt ? 1 : 0;
      }),
    }));
    return { kind: 'bar', categories, series };
  }

  const data = weekDays.map((day) => {
    const raw = valuesByFieldId(dayMap.get(day.format('YYYY-MM-DD')), field.id);
    return raw && raw.trim() !== '' ? 1 : 0;
  });
  return {
    kind: chartType === 'BAR' ? 'bar' : 'line',
    categories,
    series: [{ name: field.name, data }],
  };
};
